// Durability and repairs (owner, 7. 10. 2026). An item at 0 durability is broken but stays worn: its own stats
// (the headline stat and the affixes) count 25% less until it is repaired; its perk, its set piece and a weapon's
// class stay (no more bare hands). A broken item is repaired as a Workshop job on one bench (it cannot be worn
// meanwhile); before 0 a repair stays as it was: at once, for materials. Repair Kits restore less on better gear,
// the Precision Repair Kit 30 on any item. More wear in danger 4 and 5 zones (WEAR_RULES).
import type { Rarity } from '../types';

/**
 * A broken item (0 durability): its stats × `statMult`. `on: false` (simulations, REPAIR_OLD): the rule before
 * 7. 10. 2026, a broken item gives nothing (no stats, no perk, no set piece) and a broken weapon fights unarmed.
 */
export const BROKEN_RULES = { on: true, statMult: 0.75 };

/** A worn item under this share of its durability is flagged on the order sheet and at Camp. */
export const DURABILITY_WARN = 0.2;

/**
 * Repair of a broken item: a Workshop job on one bench (the Workshop's speed counts), the materials of a full repair
 * and, for a Legendary, Relic Shards (28. 10. 2026, owner: an Epic no longer takes any; it took 2).
 */
export const BROKEN_REPAIR = {
  minutes: { common: 10, uncommon: 30, rare: 60, epic: 120, legendary: 240 } as Record<Rarity, number>,
  shards: { legendary: 5 } as Partial<Record<Rarity, number>>,
};

/**
 * Repair Kits (owner, 7. 10. 2026): a Repair Kit restores less on better gear; a Precision Repair Kit (Electronics,
 * Chemicals, Metal) restores 30 on any item, made for Epic and Legendary gear. Neither fixes a broken item (that is the
 * Workshop's job). `old` (simulations, REPAIR_OLD): every Repair Kit 30, as before.
 */
export const REPAIR_KITS = {
  kit: { common: 30, uncommon: 25, rare: 20, epic: 15, legendary: 10 } as Record<Rarity, number>,
  precision: 30,
  old: false,
};

export type RepairKitId = 'repair_kit' | 'precision_repair_kit';

/** Durability one kit gives back to an item of this rarity. */
export function kitRestores(kit: RepairKitId, rarity: Rarity): number {
  if (kit === 'precision_repair_kit') return REPAIR_KITS.precision;
  return REPAIR_KITS.old ? 30 : REPAIR_KITS.kit[rarity];
}

/**
 * Wear by the zone's danger: every durability loss of a run (fights, PvP, events) × this. Owner, 7. 10. 2026: more wear
 * in danger 4 and 5, so that gear really breaks for survivors from level 12 who do not look after it.
 */
export const WEAR_RULES = { danger: { 1: 1, 2: 1, 3: 1, 4: 1.15, 5: 1.3 } as Record<1 | 2 | 3 | 4 | 5, number> };
// 8. 10. 2026 (owner, after the confirmation in two worlds): ×1.15 / ×1.3 (×1.5 / ×2 and ×1.25 / ×1.5 put the upkeep of
// Epic and Legendary gear over 20% for the active Gunner and Bruiser).

/**
 * 8. 10. 2026 (owner): one run takes at most this share of an item's max durability, so an item that sets out above 40%
 * cannot break on that run (a broken item comes from going out with worn gear). The order sheet still warns under 20%.
 */
export const RUN_WEAR_CAP = 0.4;

/** The durability a run may still take from an item that lost `lost` on it so far (`loss`: what this hit would take). */
export function cappedRunLoss(loss: number, maxDurability: number, lost: number): number {
  return Math.max(0, Math.min(loss, Math.floor(RUN_WEAR_CAP * maxDurability) - lost));
}

export function wearMult(danger: number): number {
  return WEAR_RULES.danger[danger as 1 | 2 | 3 | 4 | 5] ?? 1;
}

export const isBroken = (it: { durability: number }): boolean => it.durability <= 0;

/** Does a broken item still count while worn (its stats at 75%, its perk, its set piece, its weapon class)? */
export const brokenStillCounts = (): boolean => BROKEN_RULES.on;

/** Does this worn item count at all (whole, or broken under the new rule)? */
export const wornCounts = (it: { durability: number }): boolean => it.durability > 0 || BROKEN_RULES.on;
